import { Market, Markets, MarketValidation } from '@shared/config/market/market.models';
import { configBase } from '@/config/base';
import {
    informationByMarketNumber,
    marketNumberByHost,
    STENA_COMPANIES,
} from '@shared/config/market/market.mapping';
import { getStoredMarketNumber } from '@shared/helpers/localStorage';
import {
    OUR_POLICIES_PATH,
    TRAVEL_CONDITIONS_PATH,
} from '@shared/config/market/data/market.data.consts';
import {
    semboBritishCompanyData,
    semboDanishCompanyData,
    semboFinnishCompanyData,
    semboGermanCompanyData,
    semboNorwegianCompanyData,
    semboPolishCompanyData,
    semboSpanishCompanyData,
    semboSwedishCompanyData,
} from '@shared/config/market/data/market.data.sembo';

export function getMarketSpecs(market: number | null) {
    const defaultMarket = Markets.SWEDEN;

    if (market && marketExists(market)) {
        return informationByMarketNumber[market];
    }

    return informationByMarketNumber[defaultMarket];
}

function marketExists(market: number | null) {
    return (
        market !== null &&
        market in Markets &&
        Object.prototype.hasOwnProperty.call(informationByMarketNumber, market)
    );
}

export function getCurrentMarketSpecs() {
    let market: Market;

    const storedMarketNumber = getStoredMarketNumber();

    const host: string = window.location.host
        .replace('www.', '')
        .replace('whitelabel.', '')
        .toLowerCase();

    if (configBase.devMode.enabled || configBase.isTest) {
        market = getMarketSpecs(storedMarketNumber);
    } else {
        const marketNumber = marketNumberByHost[host];
        market = getMarketSpecs(marketNumber);
    }

    return market;
}

export function getTravelConditionsLink() {
    return getBackendBaseUrl() + TRAVEL_CONDITIONS_PATH;
}

export function getOurPoliciesLink() {
    return getBackendBaseUrl() + OUR_POLICIES_PATH;
}

export function getBaggageInsuranceGeneralTermsLink() {
    const insurance = getCurrentMarketSpecs().insurance;

    return insurance?.baggage?.generalTerms ?? '';
}

export function getBaggageInsurancePurchaseTermsLink() {
    const insurance = getCurrentMarketSpecs().insurance;

    return insurance?.baggage?.purchaseTerms ?? '';
}

export function getBaggageInsuranceCompensation() {
    const compensation = getCurrentMarketSpecs().insurance?.baggage?.compensation;

    return compensation && compensation.Amount > 0 ? compensation : undefined;
}

export function getTravelInsuranceGeneralTermsLink() {
    const insurance = getCurrentMarketSpecs().insurance;

    return insurance?.travel?.generalTerms ?? '';
}

export function getTravelInsurancePurchaseTermsLink() {
    const insurance = getCurrentMarketSpecs().insurance;

    return insurance?.travel?.purchaseTerms ?? '';
}

export function getTravelInsuranceCompensation() {
    const compensation = getCurrentMarketSpecs().insurance?.travel?.compensation;

    return compensation && compensation.Amount > 0 ? compensation : undefined;
}

export function getMarketPhoneNumber(
    market: Market,
    type: 'support' | 'priority'
): string | undefined {
    if (!market) return undefined;
    if (type === 'support') {
        return market.supportPhone ?? semboSwedishCompanyData.supportPhone;
    }

    const PRIORITY_SUPPORT_ENABLED_MARKETS = [
        semboSwedishCompanyData.number,
        semboDanishCompanyData.number,
        semboNorwegianCompanyData.number,
        semboFinnishCompanyData.number,
        semboSpanishCompanyData.number,
        semboGermanCompanyData.number,
        semboBritishCompanyData.number,
        semboPolishCompanyData.number,
    ];

    return PRIORITY_SUPPORT_ENABLED_MARKETS.includes(market.number)
        ? market.priorityPhone
        : undefined;
}

function getPrefix(isSubdomain: boolean) {
    const protocol = 'https://';
    const subdomain = 'www.';

    return isSubdomain ? protocol : [protocol, subdomain].join('');
}

function getDomain(specs: Market) {
    return specs.domainRedirected ? specs.domainRedirected : specs.domain;
}

function getBackendBaseUrl() {
    const specs = getCurrentMarketSpecs();
    const prefix = getPrefix(specs.isSubdomain);
    const domain = getDomain(specs);

    return prefix + domain;
}

export function isStenaCompany(companyNumber: number) {
    return STENA_COMPANIES.includes(companyNumber);
}

export function validateCompany(companyId: number, currentId: number): MarketValidation {
    const isSupportedMarket = marketExists(companyId);
    const currentMarket = getMarketSpecs(currentId);
    const providedMarket = getMarketSpecs(companyId);

    if (!isSupportedMarket) {
        return MarketValidation.NotValid;
    }

    if (configBase.devMode.enabled) {
        return MarketValidation.IsValid;
    }

    if (currentMarket.homePage !== providedMarket.homePage) {
        return MarketValidation.NeedsRedirection;
    } else {
        // since we have duplicates domains for companyId 5/405 (sembo.dk and old dtf) we need to compare homepage instead of number.
        // if we decommission one of them, we can start using number again, since it's more accurate
        return currentMarket.homePage === providedMarket.homePage
            ? MarketValidation.IsValid
            : MarketValidation.NotValid;
    }
}
